import { AEO_LLMS_PATH, getAeoMarkdownPath } from "@/config/aeo";
import { SITE_ORIGIN } from "@/config/constants";

export type DocumentSeoInput = {
  title: string;
  description: string;
  path: string;
  imageUrl?: string;
  imageAlt?: string;
  imageWidth?: number;
  imageHeight?: number;
  keywords?: string;
  robots?: string;
  siteName?: string;
  ogType?: "website" | "article";
  article?: { publishedTime: string; modifiedTime: string; section?: string; tags?: readonly string[] };
};

function pageOrigin(): string {
  if (SITE_ORIGIN) return SITE_ORIGIN;
  if (typeof window !== "undefined") return window.location.origin;
  return "";
}

function metaByProperty(property: string): HTMLMetaElement | null {
  return document.head.querySelector(`meta[property="${property}"]`);
}

function metaByName(name: string): HTMLMetaElement | null {
  return document.head.querySelector(`meta[name="${name}"]`);
}

function setMetaProperty(property: string, content: string): void {
  let el = metaByProperty(property);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.content = content;
}

/** Facebook OG allows multiple `og:locale:alternate` tags — avoid overwriting a single node. */
function ensureOgLocaleAlternate(locale: string): void {
  const existing = document.head.querySelector(
    `meta[property="og:locale:alternate"][content="${locale}"]`,
  );
  if (existing) return;
  const el = document.createElement("meta");
  el.setAttribute("property", "og:locale:alternate");
  el.content = locale;
  document.head.appendChild(el);
}

function setMetaName(name: string, content: string): void {
  let el = metaByName(name);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.content = content;
}

function toAbsoluteUrl(origin: string, url: string): string {
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const path = url.startsWith("/") ? url : `/${url}`;
  return `${origin}${path}`;
}

function setHreflangLink(lang: string, href: string): void {
  let el = document.querySelector(
    `link[data-lucentia-hreflang="${lang}"]`,
  ) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.rel = "alternate";
    el.setAttribute("hreflang", lang);
    el.setAttribute("data-lucentia-hreflang", lang);
    document.head.appendChild(el);
  }
  el.href = href;
}

function setDescribedByLink(href: string): void {
  let el = document.querySelector(
    'link[rel="describedby"][data-lucentia-llms]',
  ) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.rel = "describedby";
    el.setAttribute("data-lucentia-llms", "true");
    document.head.appendChild(el);
  }
  el.href = href;
}

function setMarkdownAlternate(href: string): void {
  let el = document.querySelector(
    'link[rel="alternate"][type="text/markdown"][data-lucentia-md]',
  ) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.rel = "alternate";
    el.type = "text/markdown";
    el.title = "Markdown for AI agents";
    el.setAttribute("data-lucentia-md", "true");
    document.head.appendChild(el);
  }
  el.href = href;
}

const JSON_LD_ID = "lucentia-json-ld";

export function applyJsonLd(data: Record<string, unknown> | null): void {
  let script = document.getElementById(JSON_LD_ID) as HTMLScriptElement | null;
  if (!data) {
    script?.remove();
    return;
  }
  if (!script) {
    script = document.createElement("script");
    script.id = JSON_LD_ID;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}

export function applyDocumentSeo(opts: DocumentSeoInput): void {
  const origin = pageOrigin();
  const path = opts.path.startsWith("/") ? opts.path : `/${opts.path}`;
  const pageUrl = `${origin}${path}`;

  document.title = opts.title;
  setMetaName("description", opts.description);

  if (opts.keywords) {
    setMetaName("keywords", opts.keywords);
  }

  setMetaName(
    "robots",
    opts.robots ?? "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
  );

  setMetaProperty("og:title", opts.title);
  setMetaProperty("og:description", opts.description);
  setMetaProperty("og:url", pageUrl);
  setMetaProperty("og:type", opts.ogType ?? "website");
  applyArticleMeta(opts.article);
  setMetaProperty("og:locale", "es_ES");
  ensureOgLocaleAlternate("es_AR");
  setMetaProperty("og:site_name", opts.siteName ?? "Lucentia Social");

  if (opts.imageUrl) {
    const abs = toAbsoluteUrl(origin, opts.imageUrl);
    setMetaProperty("og:image", abs);
    setMetaProperty("og:image:alt", opts.imageAlt ?? opts.title);
    setMetaName("twitter:image", abs);
    if (opts.imageWidth) {
      setMetaProperty("og:image:width", String(opts.imageWidth));
    }
    if (opts.imageHeight) {
      setMetaProperty("og:image:height", String(opts.imageHeight));
    }
  }

  setMetaName("twitter:card", "summary_large_image");
  setMetaName("twitter:title", opts.title);
  setMetaName("twitter:description", opts.description);

  let canonical = document.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    document.head.appendChild(canonical);
  }
  canonical.href = pageUrl;

  /* España como mercado principal; `es` amplía a hispanohablantes (p. ej. Argentina) sin URLs duplicadas */
  setHreflangLink("es-ES", pageUrl);
  setHreflangLink("es", pageUrl);
  setHreflangLink("x-default", pageUrl);

  setDescribedByLink(`${origin}${AEO_LLMS_PATH}`);
  const markdownPath = getAeoMarkdownPath(path);
  if (markdownPath) {
    setMarkdownAlternate(`${origin}${markdownPath}`);
  } else {
    document.querySelector('link[rel="alternate"][type="text/markdown"][data-lucentia-md]')?.remove();
  }
}

/* Article tags only exist on blog posts; other pages drop them. */
function applyArticleMeta(article: DocumentSeoInput["article"]): void {
  document.head.querySelectorAll('meta[property^="article:"]').forEach((el) => el.remove());
  if (!article) return;
  const add = (property: string, content: string) => {
    const el = document.createElement("meta");
    el.setAttribute("property", property);
    el.content = content;
    document.head.appendChild(el);
  };
  add("article:published_time", article.publishedTime);
  add("article:modified_time", article.modifiedTime);
  add("article:author", "Jenifer Roelstraete");
  if (article.section) add("article:section", article.section);
  for (const tag of article.tags ?? []) add("article:tag", tag);
}
